import { call, CallEffect, put, PutEffect } from 'redux-saga/effects';
import { getData, getDataWithResponseHeaders, postData, deleteData } from '../shared/fetchApi';
import {
    DELETE_USER_FAILED,
    DELETE_USER_SUCCEEDED,
    DeleteUserAction,
    GET_USER_DETAILS_FAILED,
    GET_USER_DETAILS_SUCCEEDED,
    GET_USER_FAILED,
    GET_USER_SUCCEEDED,
    GetUserAction,
    GetUserDetailsAction,
    UPDATE_USER_FAILED,
    UPDATE_USER_SUCCEEDED,
    UpdateUserAction,
} from '../store/User.store';
import { routes } from '../store/Workflow.store';
import { RegistrierungsStatus } from '../shared/types/enums';
import type { User } from '../shared/types';

/**
 * Lädt Benutzerliste. Bei listParams: serverseitige Paginierung/Sortierung/Suche (kürzere Ladezeit).
 * Backend-API (bei listParams): GET admin/users?page=0&size=10&sort=<sortColumn>,asc|desc[&search=...][&approvalRequiredOnly=true]
 * Response: JSON User[] + Header "x-total-count: <Gesamtanzahl>".
 * Sortierfelder z.B. nachname, vorname, email, registration.createdAt; Suche optional (Backend filtert).
 */
export function* getUserSaga(action: GetUserAction): Generator<PutEffect | CallEffect> {
    try {
        const base = action.registrierungsStatus === RegistrierungsStatus.NEU ? 'admin/users?approvalRequiredOnly=true' : 'admin/users';
        const separator = base.includes('?') ? '&' : '?';

        if (action.listParams) {
            const { page, size, sortColumn, sortOrder, search } = action.listParams;
            const url =
                `${base}${separator}page=${page - 1}&size=${size}&sort=${sortColumn},${sortOrder}` +
                (search ? `&search=${encodeURIComponent(search)}` : '');
            const [user, headers] = (yield call(getDataWithResponseHeaders, url)) as unknown as [User[], Headers];
            const totalCount = parseInt(headers.get('x-total-count') ?? String(user.length), 10);
            yield put({ type: GET_USER_SUCCEEDED, user, totalCount });
        } else {
            const user = yield call(getData, base);
            yield put({ type: GET_USER_SUCCEEDED, user });
        }
    } catch (e) {
        yield put({ type: GET_USER_FAILED });
    }
}

export function* getUserDetailsSaga(action: GetUserDetailsAction): Generator<PutEffect | CallEffect> {
    try {
        const url = 'admin/users/' + action.userId;
        const user = yield call(getData, url);
        yield put({ type: GET_USER_DETAILS_SUCCEEDED, user });
    } catch (e) {
        yield put({ type: GET_USER_DETAILS_FAILED });
    }
}

export function* updateUserSaga(action: UpdateUserAction): IterableIterator<PutEffect | CallEffect> {
    try {
        const payload = {
            ...action.user,
            ...(typeof action.user.disabled !== 'undefined' ? { enabled: action.user.disabled ? false : true } : {}),
        };
        const user = yield call(postData, 'admin/users/' + action.user.id, payload, 'PUT');
        yield put({ type: UPDATE_USER_SUCCEEDED, user });
        action.history.push(
            `${routes.benutzer}/${action.user.registrationValidated ? RegistrierungsStatus.REGISTRIERT : RegistrierungsStatus.NEU}`
        );
    } catch (e) {
        yield put({ type: UPDATE_USER_FAILED, user: action.user });
    }
}

export function* deleteUserSaga(action: DeleteUserAction): IterableIterator<PutEffect | CallEffect> {
    try {
        yield call(deleteData, 'admin/users/' + action.user.id);
        yield put({ type: DELETE_USER_SUCCEEDED });
        action.history.push(`${routes.benutzer}/${RegistrierungsStatus.NEU}`);
    } catch (e) {
        yield put({ type: DELETE_USER_FAILED });
    }
}
